import { Dispatch, SetStateAction, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

// assets
import successImage from 'assets/images/success-image.svg'
import loadingCalendar from 'assets/icons/loading-calendar.png'

// local imports
import AuthUtil from 'utils/AuthUtil'
import { IDoctorFilterForm, IDoctorSearchForm } from 'types/HealthType'
import { ISideBarModel } from 'types/CalendarType'
import {
  useAppointmentModal,
  useGetDoctorList,
  useGetDoctorSpecialtyList,
  useGetLocationList,
} from 'components/home/on-campus-services/health-service/hooks'
import DoctorSearchForm from './DoctorSearchForm'
import CalendarDocAppointmentModal from './CalendarDocAppointmentModal'
import NotificationDialog from 'components/ui/NotificationDialog'
import Loader from 'components/ui/Loader'
import FormModal from 'components/ui/modal/FormModal'

interface ModalProps {
  modal: boolean
  setModal: Dispatch<SetStateAction<boolean>>
  closeDoctorModal: () => void
  selectedDay: ISideBarModel | undefined
  resetAll: Dispatch<SetStateAction<boolean>>
  sidebarAfterSave: () => void
}

export default function DoctorSelectModal({
  modal,
  setModal,
  selectedDay,
  resetAll,
  closeDoctorModal,
  sidebarAfterSave,
}: ModalProps) {
  const { t } = useTranslation()
  const selectedDayDate = moment(selectedDay?.day.date).toDate()
  const selectedDateStr = moment(selectedDayDate).format('DD.MM.YYYY')

  /* Get Current logged employee infos - (location id) */
  const employeeLocation = AuthUtil.getEmployeeSummary()?.locationId

  /* Filter form default values */
  const defaultValues: IDoctorSearchForm = {
    locationId: employeeLocation ?? 0,
    specialtyId: 0,
    reservationDate: selectedDayDate,
    doctor: null,
  }
  const filteredDate = defaultValues.reservationDate

  /* Filter form state  */
  const { watch, resetField, control } = useForm<IDoctorSearchForm>({
    defaultValues,
  })

  /* Get doctor search form values */
  const locationId = watch('locationId')
  const specialtyId = watch('specialtyId')
  const selectedDoctor = watch('doctor')

  /* Get Doctor List Get Doctors Specialties - Locations | Custom Hooks */
  const { getDoctorList, doctors, loading } = useGetDoctorList()
  const { data: specialtyList } = useGetDoctorSpecialtyList(locationId)
  const { data: locationList } = useGetLocationList()

  /* Handle Form Submit */
  const onSubmit = (data: IDoctorFilterForm) => {
    getDoctorList(data)
  }

  /* Get Doctors List on initial page load  */
  useEffect(() => {
    onSubmit(defaultValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    onSubmit({ locationId, specialtyId, reservationDate: selectedDayDate })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, specialtyId])

  /* Appointment Modal (Open - Close, Selected Doctor). | Custom Hook */
  const {
    openModal,
    closeModal,
    modal: isOpen,
    selectedDoc,
    setSelectedDoc,
    notification,
    setNotification,
  } = useAppointmentModal()

  /* Search Modal Methods */
  const handleOnCancel = () => closeDoctorModal()
  const selectDoctorAndOpen = () => {
    if (!!selectedDoctor) {
      openModal(selectedDoctor)
      setModal(false)
    }
  }

  /* Appointment Scheduler Modal */
  const handleOnCancelAppointmentModal = () => {
    closeModal()
    setModal(true)
  }

  /* Notification Modal */
  const handleCloseNotification = () => {
    setModal(false)
    setNotification(false)
    resetAll(false)
  }

  return (
    <>
      <FormModal
        id="doctorSelectionModal"
        closable
        header={<span>{t('appointment-modal.doctor-appointment')} (1/2)</span>}
        visible={modal}
        onHide={handleOnCancel}
        onCancel={handleOnCancel}
        onSave={selectDoctorAndOpen}
        disabled={!selectedDoctor}>
        <ModalHeader>
          <span className="doc-message">{t('calendar.selected-day-info-text', { date: selectedDateStr })}</span>
        </ModalHeader>

        <DoctorSearchForm
          control={control}
          resetField={resetField}
          doctorList={doctors}
          specialtyList={specialtyList}
          locationList={locationList}
        />

        {loading ? (
          <div className="my-5 w-100">
            <Loader src={loadingCalendar} />
          </div>
        ) : null}
      </FormModal>

      {/* Doctor Appointment Modal */}
      {!!selectedDoc ? (
        <CalendarDocAppointmentModal
          selectedDateStr={selectedDateStr}
          doctors={doctors}
          open={isOpen}
          openModal={openModal}
          closeModal={closeModal}
          cancelAppointmentModal={handleOnCancelAppointmentModal}
          showNotification={() => {
            setNotification(true)
            sidebarAfterSave()
          }}
          setSelectedDoc={setSelectedDoc}
          selectedDoc={selectedDoc}
          filterDate={filteredDate}
        />
      ) : null}

      <NotificationDialog
        display={notification}
        imgSrc={successImage}
        goHomeButton={true}
        title={t('success-text.appointment-completed')}
        content={t('appointment-modal.notification-text')}
        firstButtonLabel={t('buttons.back-to-calendar')}
        onHide={handleCloseNotification}
        onSubmit={handleCloseNotification}
        selectedType={null}
        setSelectedType={undefined}
        types={[]}
      />
    </>
  )
}

const ModalHeader = styled.div`
  margin-bottom: 20px;
  .doc-message {
    font-size: 14px;
    color: #222e47;
  }
`
